import { useState } from 'react';
import { addSeconds, getNow, useReactForm } from '@ninja/utilities';
import {
	AddMemberDetailsRequest,
	AddMemberDetailsV2Request,
	GetDocumentRetrieveAllResponse,
	GetMemberValidationStatusError,
	GlobalAPIError,
	addMemberDetails,
	addMemberDetailsV2,
	getMemberValidationStatus,
	getMemberValidationStatusV2,
} from '@shory-health-individual/customer-api';
import { DataErrorAlertProps, HasEmiratesResponse, getMemberIndexes } from '../../../../common';
import { useAppPlatformContextOptions } from '../../../../contexts/app';
import { JourneyDetails } from '../../../../server';
import { selectSetDocumentError, selectSetPendingUnPaidFinesError, useQuoteStore } from '../../../../stores';
import { usePlans, useSystemConfigurationSettings } from '../../../app-init-data';
import useGetValidationSchema from './fields-structures/useGetValidationSchema';
import { getInitialValues } from './helpers/get-initial-values';
import { DocumentsForm } from './types';
import useUploadFiles from './useUploadFiles';

type UseDocumentsFormProps = {
	journey: JourneyDetails;
	onSuccess: ({ journeyId }: { journeyId: string }) => void;
	memberDocuments: GetDocumentRetrieveAllResponse;
	onDocumentError: (errorCode: string) => Promise<void>;
	isEidFlow?: boolean;
	navigateToMembers: () => void;
};

export const useDocumentsForm = ({
	journey,
	onSuccess,
	memberDocuments,
	onDocumentError,
	isEidFlow,
	navigateToMembers,
}: UseDocumentsFormProps) => {
	const { apiConnectionOptions, onGeneralErrorHandling } = useAppPlatformContextOptions();
	const { systemConfigurationSettings } = useSystemConfigurationSettings();
	const getValidationSchema = useGetValidationSchema(isEidFlow);
	const setDocumentError = useQuoteStore(selectSetDocumentError);
	const versionedGetMemberValidationStatus = () =>
		isEidFlow ? getMemberValidationStatusV2 : getMemberValidationStatus;
	const isSponsorLaborContractRequired = usePlans().getPlanById(
		journey.planId as number
	)?.isSponsorLaborContractRequired;

	const [showAlert, setShowAlert] = useState<DataErrorAlertProps | undefined>(undefined);
	const [isOpen, setIsOpen] = useState(false);
	const [validationStatusCheckTimer, setValidationStatusCheckTimer] = useState(0);
	const apiTimeOutInSeconds = systemConfigurationSettings?.apiTimeOutInSeconds ?? 0;

	const versionedAddMemberDetails = (req: AddMemberDetailsV2Request) =>
		isEidFlow ? addMemberDetailsV2(apiConnectionOptions)(req) : addMemberDetails(apiConnectionOptions)(req);
	const setPendingUnPaidFinesError = useQuoteStore(selectSetPendingUnPaidFinesError);

	const form = useReactForm<DocumentsForm>({
		initialValues: getInitialValues(
			isEidFlow,
			[journey.principalDetails, ...(journey.dependents ?? [])],
			memberDocuments
		),
		validationMode: 'zod',
		validation: (formValues) =>
			getValidationSchema({
				members: [{ ...journey.principalDetails }, ...(journey.dependents ?? [])],
				policyType: journey.policyType,
				planCategory: journey.planCategoryIdForSelectedPlan,
				formValues,
				isSponsorLaborContractRequired,
			}),
		validateBehavior: 'on-submit',
		onFormSubmit: async (formValues) => {
			try {
				const principalFormValue = formValues[journey.principalDetails.memberId];

				const dependentMemberIds = Object.keys(formValues)?.filter((m) => m !== journey.principalDetails.memberId);

				const dependents = dependentMemberIds?.map((depMemberId) => {
					const dependentDetails = formValues[depMemberId];
					const dependentHasEmiratesId = dependentDetails.hasEmiratesId === HasEmiratesResponse.Yes;

					return {
						memberId: depMemberId,
						passportNumber: dependentDetails.passportNumber!,
						visaFileNumber: dependentDetails.visaFileNumber!,
						emiratesIdNumber: dependentHasEmiratesId ? +`784${dependentDetails.emiratesId}` : undefined,
						occupationId: dependentDetails.occupationId,
						firstName: dependentHasEmiratesId ? undefined : dependentDetails.firstName,
						lastName: dependentHasEmiratesId ? undefined : dependentDetails.lastName,
						unifiedNumber: dependentHasEmiratesId ? undefined : dependentDetails.unifiedNumber,
						uploadRef: memberDocuments[depMemberId]?.uploadRef ?? '',
					};
				});

				const principalHasEmiratesId = principalFormValue.hasEmiratesId === HasEmiratesResponse.Yes;
				const requestBody: AddMemberDetailsRequest = {
					encryptedJourneyId: journey.encryptedJourneyId,
					principalSalary: principalFormValue.salaryBand,
					principalDetails: {
						memberId: journey.principalDetails.memberId,
						passportNumber: principalFormValue.passportNumber!,
						visaFileNumber: principalFormValue.visaFileNumber!,
						emiratesIdNumber: principalHasEmiratesId ? +`784${principalFormValue.emiratesId}` : undefined,
						occupationId: principalFormValue.occupationId,
						firstName: principalHasEmiratesId ? undefined : principalFormValue.firstName,
						lastName: principalHasEmiratesId ? undefined : principalFormValue.lastName,
						unifiedNumber: principalHasEmiratesId ? undefined : principalFormValue.unifiedNumber,
						uploadRef: memberDocuments[journey.principalDetails.memberId]?.uploadRef ?? '',
					},
					dependents,
				};

				const { isSuccess } = await versionedAddMemberDetails(requestBody);

				if (isSuccess) {
					setValidationStatusCheckTimer(addSeconds(getNow(), apiTimeOutInSeconds).getTime());
					setIsOpen(true);
					await versionedGetMemberValidationStatus()(apiConnectionOptions, apiTimeOutInSeconds * 1000)({
						parameters: { encryptedJourneyId: journey.encryptedJourneyId },
					});

					setIsOpen(false);
					setDocumentError(undefined);
					await onSuccess({ journeyId: journey.encryptedJourneyId });
				}
			} catch (error) {
				const errors = (error as GetMemberValidationStatusError).mapped;

				setIsOpen(false);

				if (errors.some((error) => ['InvalidVisaEmirateSelection', 'BlackListedMember'].includes(error.code))) {
					await onDocumentError(errors[0].code);
				} else if (errors.some((error) => error.code === 'InvalidDataError')) {
					if (isEidFlow) {
						setDocumentError('InvalidDataError');
						navigateToMembers();
					} else {
						await onDocumentError(errors[0].code);
					}
				} else if (errors.some((error) => error.code === 'PendingUnPaidFines')) {
					const error = errors.find((error) => error.code === 'PendingUnPaidFines')!;

					setPendingUnPaidFinesError({
						errorCode: error.code,
						errorMessage: error.message!,
					});
					await onDocumentError('PendingUnPaidFines');
				} else if (errors.some((error) => error.code === 'DataEntryError')) {
					if (isEidFlow) {
						setDocumentError('DataEntryError');
						navigateToMembers();
					} else {
						setShowAlert(DataErrorAlertProps.WrongDataEntry);
					}
				} else if (errors.some((error) => error.code === 'InvalidMemberData')) {
					if (isEidFlow) {
						setDocumentError('InvalidMemberData');
						navigateToMembers();
					} else {
						setShowAlert(DataErrorAlertProps.InvalidMemberData);
					}
				} else if (errors.some((error) => error.code === 'TechnicalError')) {
					if (isEidFlow) {
						setDocumentError('TechnicalError');
						navigateToMembers();
					} else {
						setShowAlert(DataErrorAlertProps.DataError);
					}
				} else if (errors.some((error) => error.code === 'InvalidVisaNumber')) {
					setShowAlert(DataErrorAlertProps.WrongDataEntry);
				} else if (errors.some((error) => error.code === 'TimeoutError' || error.code === 'ApiTimeOut')) {
					setShowAlert(DataErrorAlertProps.DataError);
				} else if (errors.some((error) => error.code === 'PolicyDetailsNotFound')) {
					onDocumentError('PolicyDetailsNotFound');
				} else if (errors.some((error) => error.code === 'EffectiveDateExceedValidationDate')) {
					onDocumentError('EffectiveDateExceedValidationDate');
				} else if (errors.some((error) => ['SanctionedMember'].includes(error.code))) {
					await onDocumentError('SanctionedMember');
				} else if (errors.some((error) => error.code === 'InvalidQuoteEmirateToVisaFileNumber')) {
					onDocumentError('InvalidQuoteEmirateToVisaFileNumber');
				} else {
					await onGeneralErrorHandling(error as GlobalAPIError);
				}
			}
		},
	});

	const { onUploadFile, validateFile, onClearFile, getFileValue, getFileError, getDocumentConfigs } = useUploadFiles({
		encryptedJourneyId: journey.encryptedJourneyId,
		memberDocuments,
		form,
	});

	const memberIndexes = getMemberIndexes(journey?.dependents ?? [], 'relationshipTypeId', 'memberId');

	return {
		form,
		validationStatusCheckTimer,
		isOpen,
		showAlert,
		memberIndexes,
		onUploadFile,
		validateFile,
		onClearFile,
		getFileValue,
		getFileError,
		getDocumentConfigs,
		isSponsorLaborContractRequired,
	};
};
